import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import {
  Save,
  X,
  AlertCircle,
  CheckCircle2,
  FileText,
  ArrowRight,
  ArrowLeft,
  Calendar,
  User,
  Building,
  Phone,
  ClipboardList,
  Layers,
  Edit,
  Check,
  XCircle,
  AlignLeft,
  Tag,
  CheckSquare,
  Plus,
  Image as ImageIcon,
  MoreHorizontal,
  ChevronDown,
  MessageSquare,
} from "lucide-react";
import { Tarefa, PerguntaFormulario, RespostaFormulario, BlocoPerguntas, Template } from '@/types/tarefas';
import { formatDate, formatPhone, getEtapaNome, calcularFluxoPerguntas, respostasParaMapa, calcularEstatisticasFluxo, determinarProximaPergunta, expandirPerguntasComBlocos, avaliarConclusoesAutomaticas } from '@/utils/tarefas';
import { getKanbanColumnForTask, type KanbanColumnId } from '@/components/Tarefas/TarefasKanbanBoard';
import { cn } from '@/lib/utils';

const KANBAN_LIST_LABELS: Record<KanbanColumnId, string> = {
  automaticas: 'Automáticas (templates)',
  pendente: 'Pendente',
  em_andamento: 'Em andamento',
  concluidas: 'Concluídas',
  canceladas: 'Canceladas',
};

function iniciaisNome(nome: string): string {
  const p = nome.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

const mapasIguais = (a: Record<string, string>, b: Record<string, string>) => {
  const chavesA = Object.keys(a);
  const chavesB = Object.keys(b);
  if (chavesA.length !== chavesB.length) {
    return false;
  }
  return chavesA.every((chave) => a[chave] === b[chave]);
};

const conjuntosIguais = (a: Set<string>, b: Set<string>) => {
  if (a.size !== b.size) {
    return false;
  }
  for (const valor of a) {
    if (!b.has(valor)) {
      return false;
    }
  }
  return true;
};

interface PreencherFormularioModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tarefa: Tarefa | null;
  onSuccess: () => void;
  isLoading?: boolean;
  /** Abrir ficha do cliente (ex.: a partir do menu do cartão). */
  onVerLead?: (leadId: string) => void;
}

export function PreencherFormularioModal({
  open,
  onOpenChange,
  tarefa,
  onSuccess,
  isLoading = false,
  onVerLead,
}: PreencherFormularioModalProps) {
  const [respostas, setRespostas] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [perguntaAtualId, setPerguntaAtualId] = useState<string | null>(null);
  const [perguntasRespondidas, setPerguntasRespondidas] = useState<Set<string>>(new Set());
  const [fluxoPerguntas, setFluxoPerguntas] = useState<string[]>([]);
  const [notas, setNotas] = useState<string>('');
  const [blocosMap, setBlocosMap] = useState<Map<string, BlocoPerguntas>>(new Map());
  const [perguntasExpandidas, setPerguntasExpandidas] = useState<PerguntaFormulario[]>([]);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [template, setTemplate] = useState<Template | null>(null);
  const [tituloEdit, setTituloEdit] = useState('');
  const [descricaoEdit, setDescricaoEdit] = useState('');
  const [metaSaving, setMetaSaving] = useState(false);
  const [activityDetails, setActivityDetails] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!open || !tarefa) return;
    setTituloEdit(tarefa.titulo);
    setDescricaoEdit((tarefa.descricao || tarefa.instrucoes || '').trim() ? (tarefa.descricao || tarefa.instrucoes || '') : '');
  }, [open, tarefa?.id]);

  const salvarMetaAvulsa = useCallback(
    async (patch: { titulo?: string; descricao?: string }) => {
      if (!tarefa?.id || tarefa.template_id) return;
      setMetaSaving(true);
      try {
        const res = await fetchWithAuth(`/api/tarefas/${tarefa.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error((body as { error?: string }).error || 'Erro ao salvar');
        }
        onSuccess();
        toast({ title: 'Alterações guardadas' });
      } catch (e: unknown) {
        toast({
          title: 'Erro',
          description: e instanceof Error ? e.message : 'Não foi possível salvar',
          variant: 'destructive',
        });
      } finally {
        setMetaSaving(false);
      }
    },
    [tarefa?.id, tarefa?.template_id, onSuccess, toast]
  );

  const buscarDetalhesTarefa = async (tarefaId: string): Promise<Tarefa | null> => {
    try {
      const response = await fetchWithAuth(`/api/tarefas/${tarefaId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar detalhes da tarefa');
      }

      const dadosAtualizados: Tarefa = await response.json();
      return dadosAtualizados;
    } catch (error) {
      console.error('Erro ao carregar detalhes da tarefa:', error);
      return null;
    }
  };

  const renderStatusBadge = (status: Tarefa['status']) => {
    switch (status) {
      case 'concluida':
        return <Badge className="bg-green-500 text-white">Concluída</Badge>;
      case 'concluida_com_pendencias':
        return <Badge className="bg-yellow-600 text-white">Concluída com Pendências</Badge>;
      case 'em_andamento':
        return <Badge className="bg-blue-500 text-white">Em Andamento</Badge>;
      case 'atrasada':
        return <Badge className="bg-red-500 text-white">Atrasada</Badge>;
      case 'pendente':
        return <Badge className="bg-yellow-500 text-white">Pendente</Badge>;
      case 'cancelada':
        return <Badge variant="secondary">Cancelada</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const renderPrioridadeBadge = (prioridade: Tarefa['prioridade']) => {
    switch (prioridade) {
      case 'urgente':
        return <Badge className="bg-red-600 text-white">Urgente</Badge>;
      case 'alta':
        return <Badge className="bg-orange-500 text-white">Alta</Badge>;
      case 'media':
        return <Badge className="bg-yellow-500 text-white">Média</Badge>;
      case 'baixa':
      default:
        return <Badge variant="secondary">Baixa</Badge>;
    }
  };


  const atualizarFluxoEEstados = useCallback((perguntas: PerguntaFormulario[], respostasMap: Record<string, string>, perguntaAtualIdLocal: string | null, blocosMapLocal?: Map<string, BlocoPerguntas>, perguntasExpandidasLocal?: PerguntaFormulario[]) => {
    // Usar perguntas expandidas se disponíveis, senão usar perguntas originais
    const perguntasParaCalcular = (perguntasExpandidasLocal && perguntasExpandidasLocal.length > 0) ? perguntasExpandidasLocal : perguntas;
    const fluxo = calcularFluxoPerguntas(perguntasParaCalcular, respostasMap, blocosMapLocal);
    setFluxoPerguntas(fluxo);

    const respostasFiltradas = Object.fromEntries(
      Object.entries(respostasMap).filter(([_, valor]) => valor && valor.trim() !== '')
    );
    setRespostas((prev) => (mapasIguais(prev, respostasFiltradas) ? prev : respostasFiltradas));

    const respondidas = new Set<string>(Object.keys(respostasFiltradas));
    setPerguntasRespondidas((prev) => (conjuntosIguais(prev, respondidas) ? prev : respondidas));

    const estatisticas = calcularEstatisticasFluxo(fluxo, respostasFiltradas, perguntasParaCalcular);

    if (fluxo.length === 0) {
      if (perguntaAtualIdLocal !== null) {
        setPerguntaAtualId(null);
      }
      return {
        fluxo,
        respostas: respostasFiltradas,
        respondidas,
        proximaPerguntaId: null
      };
    }

    if (estatisticas.todasRespondidas) {
      if (perguntaAtualIdLocal !== null) {
        setPerguntaAtualId(null);
      }
      return {
        fluxo,
        respostas: respostasFiltradas,
        respondidas,
        proximaPerguntaId: null
      };
    }

    const primeiraNaoRespondida = estatisticas.primeiraNaoRespondida || fluxo[0] || null;

    if (!perguntaAtualIdLocal || !fluxo.includes(perguntaAtualIdLocal)) {
      if (primeiraNaoRespondida && primeiraNaoRespondida !== perguntaAtualIdLocal) {
        setPerguntaAtualId(primeiraNaoRespondida);
      }
      return {
        fluxo,
        respostas: respostasFiltradas,
        respondidas,
        proximaPerguntaId: primeiraNaoRespondida
      };
    }

    if (respondidas.has(perguntaAtualIdLocal)) {
      // A navegação dinâmica agora é feita diretamente nas funções de resposta
      // Aqui apenas mantemos a pergunta atual se ela foi respondida
      return {
        fluxo,
        respostas: respostasFiltradas,
        respondidas,
        proximaPerguntaId: perguntaAtualIdLocal
      };
    }

    return {
      fluxo,
      respostas: respostasFiltradas,
      respondidas,
      proximaPerguntaId: perguntaAtualIdLocal
    };
  }, []);

  // Buscar blocos referenciados nas perguntas
  useEffect(() => {
    const buscarBlocosReferenciados = async () => {
      if (!tarefa || !open || !tarefa.perguntas_formulario) {
        return;
      }

      const blocosIds = new Set<string>();
      
      // Coletar todos os IDs de blocos referenciados
      tarefa.perguntas_formulario.forEach(pergunta => {
        if (pergunta.proximo_bloco_id) {
          blocosIds.add(pergunta.proximo_bloco_id);
        }
        if (Array.isArray(pergunta.regras_navegacao)) {
          pergunta.regras_navegacao.forEach(regra => {
            if (regra.proximo_bloco_id) {
              blocosIds.add(regra.proximo_bloco_id);
            }
          });
        }
      });

      if (blocosIds.size === 0) {
        setBlocosMap(new Map());
        setPerguntasExpandidas(tarefa.perguntas_formulario);
        return;
      }

      // Buscar todos os blocos referenciados
      const blocosMapLocal = new Map<string, BlocoPerguntas>();
      const promises = Array.from(blocosIds).map(async (blocoId) => {
        try {
          const response = await fetchWithAuth(`/api/blocos-perguntas/${blocoId}`);
          if (response.ok) {
            const bloco: BlocoPerguntas = await response.json();
            blocosMapLocal.set(blocoId, bloco);
          }
        } catch (error) {
          console.error(`Erro ao buscar bloco ${blocoId}:`, error);
        }
      });

      await Promise.all(promises);
      setBlocosMap(blocosMapLocal);

      // Expandir perguntas com blocos
      const perguntasExpandidasLocal = expandirPerguntasComBlocos(tarefa.perguntas_formulario, blocosMapLocal);
      setPerguntasExpandidas(perguntasExpandidasLocal);
    };

    buscarBlocosReferenciados();
  }, [tarefa?.id, open]);

  useEffect(() => {
    if (tarefa && open) {
      // Sempre carregar respostas salvas quando o modal é aberto ou quando a tarefa é atualizada
      const respostasExistentes = tarefa.respostas_formulario
        ? respostasParaMapa(tarefa.respostas_formulario)
        : {};

      // Se não há perguntas expandidas ainda, usar as originais (caso não haja blocos)
      const perguntasParaUsar = perguntasExpandidas.length > 0 ? perguntasExpandidas : (tarefa.perguntas_formulario || []);
      
      if (perguntasParaUsar.length === 0) {
        return;
      }

      // Sempre usar as respostas salvas quando o modal é aberto ou quando a tarefa é atualizada
      // Isso garante que o progresso salvo seja carregado corretamente
      // Só atualizar se as respostas mudaram para evitar loops infinitos
      if (!mapasIguais(respostas, respostasExistentes)) {
        setRespostas(respostasExistentes);
      }

      // Atualizar estados com as respostas salvas
      atualizarFluxoEEstados(perguntasParaUsar, respostasExistentes, perguntaAtualId, blocosMap, perguntasExpandidas);
    } else if (!open) {
      // Limpar estados apenas quando o modal é fechado completamente
      setFluxoPerguntas([]);
      setPerguntasRespondidas(new Set());
      setPerguntaAtualId(null);
      setModoEdicao(false);
      // Não limpar respostas aqui - elas serão recarregadas quando o modal for reaberto
      // Não limpar blocosMap e perguntasExpandidas para evitar recarregar desnecessariamente
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tarefa?.id, tarefa?.respostas_formulario, open, perguntasExpandidas.length, blocosMap.size]);

  useEffect(() => {
    if (open && tarefa) {
      setNotas(tarefa.notas ?? '');
      // Debug: verificar se a conclusão está presente
      if (tarefa.conclusao_automatica) {
        console.log('✅ Conclusão automática encontrada:', tarefa.conclusao_automatica);
      } else {
        console.log('⚠️ Nenhuma conclusão automática encontrada na tarefa');
      }
    }
  }, [open, tarefa?.id, tarefa?.notas, tarefa?.conclusao_automatica]);

  useEffect(() => {
    if (!open) {
      setNotas('');
    }
  }, [open]);

  // Buscar template quando a tarefa for carregada
  useEffect(() => {
    const buscarTemplate = async () => {
      if (!tarefa?.template_id || !open) {
        setTemplate(null);
        return;
      }

      try {
        const response = await fetchWithAuth(`/api/tarefas-templates/${tarefa.template_id}`);
        if (response.ok) {
          const templateData: Template = await response.json();
          setTemplate(templateData);
          
          // Nota: Removida a reavaliação automática de conclusões durante a visualização
          // A conclusão deve ser avaliada apenas quando o formulário é salvo pela primeira vez
          // Isso evita problemas de fechamento do modal durante visualização
        }
      } catch (error) {
        console.error('Erro ao buscar template:', error);
        setTemplate(null);
      }
    };

    buscarTemplate();
     
  }, [tarefa?.template_id, tarefa?.id, tarefa?.status, tarefa?.conclusao_automatica, tarefa?.respostas_formulario, open]);


  // Função removida - navegação agora é baseada nas respostas, não no fluxo sequencial

  const handleRespostaMultiplaEscolha = (perguntaId: string, resposta: string) => {
    if (!tarefa) {
      return;
    }

    const respostasAtualizadas = {
      ...respostas,
      [perguntaId]: resposta
    };

    // Encontrar a pergunta atual (pode estar nas perguntas expandidas ou originais)
    const perguntasParaBuscar = perguntasExpandidas.length > 0 ? perguntasExpandidas : (tarefa.perguntas_formulario || []);
    const perguntaAtual = perguntasParaBuscar.find(p => p.id === perguntaId);

    if (!perguntaAtual) return;

    // IMPORTANTE: Recalcular o fluxo de perguntas com as respostas atualizadas
    // Isso garante que o fluxo reflita as mudanças causadas pela resposta atual
    // atualizarFluxoEEstados já atualiza os estados de respostas e perguntasRespondidas internamente
    // NÃO navegar automaticamente - a navegação deve ser feita apenas pelo botão "Próxima"
    atualizarFluxoEEstados(
      tarefa.perguntas_formulario || [],
      respostasAtualizadas,
      perguntaId,
      blocosMap,
      perguntasExpandidas.length > 0 ? perguntasExpandidas : undefined
    );
  };

  const handleRespostaDiscursiva = (perguntaId: string, resposta: string) => {
    setRespostas(prev => ({
      ...prev,
      [perguntaId]: resposta
    }));

    setPerguntasRespondidas(prev => {
      const novoSet = new Set(prev);
      if (resposta && resposta.trim() !== '') {
        novoSet.add(perguntaId);
      } else {
        novoSet.delete(perguntaId);
      }
      return novoSet;
    });
  };

  const handleRespostaDiscursivaBlur = (perguntaId: string, resposta: string) => {
    if (!tarefa) {
      return;
    }

    if (resposta && resposta.trim() !== '') {
      // Atualizar respostas com a resposta atual
      const respostasAtualizadas = {
        ...respostas,
        [perguntaId]: resposta
      };

      // Encontrar a pergunta atual (pode estar nas perguntas expandidas ou originais)
      const perguntasParaBuscar = perguntasExpandidas.length > 0 ? perguntasExpandidas : (tarefa.perguntas_formulario || []);
      const perguntaAtual = perguntasParaBuscar.find(p => p.id === perguntaId);

      if (!perguntaAtual) return;

      // IMPORTANTE: Recalcular o fluxo de perguntas com as respostas atualizadas
      const resultadoFluxo = atualizarFluxoEEstados(
        tarefa.perguntas_formulario || [],
        respostasAtualizadas,
        perguntaId,
        blocosMap,
        perguntasExpandidas.length > 0 ? perguntasExpandidas : undefined
      );

      // Usar a nova função para determinar a próxima pergunta baseada na resposta
      const proximaPerguntaId = determinarProximaPergunta(
        perguntaAtual,
        resposta,
        perguntasParaBuscar,
        blocosMap
      );

      // Verificar se a próxima pergunta está no fluxo recalculado antes de navegar
      if (proximaPerguntaId && proximaPerguntaId !== perguntaId) {
        // Verificar se a próxima pergunta está no fluxo recalculado
        if (resultadoFluxo.fluxo.includes(proximaPerguntaId)) {
          setPerguntaAtualId(proximaPerguntaId);
        } else {
          // Se não está no fluxo, usar a primeira não respondida do fluxo
          const primeiraNaoRespondida = resultadoFluxo.proximaPerguntaId;
          if (primeiraNaoRespondida) {
            setPerguntaAtualId(primeiraNaoRespondida);
          } else {
            setPerguntaAtualId(null);
          }
        }
      } else if (!proximaPerguntaId) {
        // Se não há próxima pergunta determinada, usar a primeira não respondida do fluxo
        const primeiraNaoRespondida = resultadoFluxo.proximaPerguntaId;
        if (primeiraNaoRespondida) {
          setPerguntaAtualId(primeiraNaoRespondida);
        } else {
          setPerguntaAtualId(null);
        }
      }
    }
  };

  const handlePerguntaAnterior = () => {
    if (!perguntaAtualId || !fluxoPerguntas.length) return;

    const indiceAtual = fluxoPerguntas.indexOf(perguntaAtualId);

    if (indiceAtual > 0) {
      // Voltar para a pergunta anterior no fluxo
      setPerguntaAtualId(fluxoPerguntas[indiceAtual - 1]);
    }
  };

  const handleProximaPergunta = () => {
    if (!perguntaAtualId || !fluxoPerguntas.length || !tarefa) return;

    // Encontrar a pergunta atual (pode estar nas perguntas expandidas ou originais)
    const perguntasParaBuscar = perguntasExpandidas.length > 0 ? perguntasExpandidas : (tarefa.perguntas_formulario || []);
    const perguntaAtual = perguntasParaBuscar.find(p => p.id === perguntaAtualId);

    if (!perguntaAtual) return;

    // Obter a resposta atual da pergunta
    const respostaAtual = respostas[perguntaAtualId];

    if (!respostaAtual || respostaAtual.trim() === '') {
      // Se não há resposta, não avançar
      return;
    }

    // Recalcular o fluxo com as respostas atuais
    const resultadoFluxo = atualizarFluxoEEstados(
      tarefa.perguntas_formulario || [],
      respostas,
      perguntaAtualId,
      blocosMap,
      perguntasExpandidas.length > 0 ? perguntasExpandidas : undefined
    );

    // Determinar a próxima pergunta baseada na resposta atual
    const proximaPerguntaId = determinarProximaPergunta(
      perguntaAtual,
      respostaAtual,
      perguntasParaBuscar,
      blocosMap
    );

    // Navegar para a próxima pergunta se ela estiver no fluxo
    if (proximaPerguntaId && proximaPerguntaId !== perguntaAtualId) {
      if (resultadoFluxo.fluxo.includes(proximaPerguntaId)) {
        setPerguntaAtualId(proximaPerguntaId);
      } else {
        // Se não está no fluxo, usar a primeira não respondida do fluxo
        const primeiraNaoRespondida = resultadoFluxo.proximaPerguntaId;
        if (primeiraNaoRespondida) {
          setPerguntaAtualId(primeiraNaoRespondida);
        } else {
          setPerguntaAtualId(null);
        }
      }
    } else if (!proximaPerguntaId) {
      // Se não há próxima pergunta determinada, usar a primeira não respondida do fluxo
      const primeiraNaoRespondida = resultadoFluxo.proximaPerguntaId;
      if (primeiraNaoRespondida) {
        setPerguntaAtualId(primeiraNaoRespondida);
      } else {
        setPerguntaAtualId(null);
      }
    }
  };

  const validarFormulario = (): { valido: boolean; pendencias: string[] } => {
    const pendencias: string[] = [];

    if (!tarefa?.perguntas_formulario || tarefa.perguntas_formulario.length === 0 || fluxoPerguntas.length === 0) {
      return { valido: true, pendencias: [] };
    }

    // O fluxo já para automaticamente quando uma resposta leva ao fim
    // Então basta verificar se todas as perguntas do fluxo foram respondidas
    fluxoPerguntas.forEach((id) => {
      const pergunta = tarefa.perguntas_formulario?.find(p => p.id === id);
      if (!pergunta) return;

      const resposta = respostas[pergunta.id];

      // Todas as perguntas do fluxo são obrigatórias
      if (!resposta || resposta.trim() === '') {
        pendencias.push(pergunta.texto);
      }
    });

    return {
      valido: pendencias.length === 0,
      pendencias
    };
  };

  // Verifica se há conteúdo para salvar (respostas ou notas)
  const podeSalvar = (): boolean => {
    if (!tarefa) return false;
    
    // Verificar se há pelo menos uma resposta preenchida
    const temRespostas = Object.values(respostas).some(resposta => resposta && resposta.trim() !== '');
    
    // Verificar se há notas preenchidas
    const temNotas = notas && notas.trim() !== '';
    
    return temRespostas || temNotas;
  };

  // Verifica se o formulário está completamente preenchido
  // O formulário está completo quando todas as perguntas do fluxo foram respondidas
  // e não há mais perguntas pendentes no fluxo
  const formularioCompleto = (): boolean => {
    if (!tarefa || fluxoPerguntas.length === 0) {
      return false;
    }

    // Usar as perguntas expandidas se disponíveis
    const perguntasParaUsar = perguntasExpandidas.length > 0 ? perguntasExpandidas : (tarefa.perguntas_formulario || []);
    
    // Calcular estatísticas do fluxo atual
    const estatisticas = calcularEstatisticasFluxo(fluxoPerguntas, respostas, perguntasParaUsar);
    
    // O formulário está completo quando todas as perguntas do fluxo foram respondidas
    // e não há primeira pergunta não respondida
    return estatisticas.todasRespondidas && !estatisticas.primeiraNaoRespondida;
  };

  const handleSalvar = async (concluir: boolean = false) => {
    if (!tarefa || isLoading) {
      return;
    }

    // Verificar se há perguntas no formulário
    const temPerguntas = (tarefa.perguntas_formulario && tarefa.perguntas_formulario.length > 0) || perguntasOrdenadas.length > 0;

    // Verificar se há conteúdo para salvar (só necessário se houver perguntas)
    // Se não houver perguntas, permitir salvar mesmo sem conteúdo (apenas notas ou concluir diretamente)
    if (temPerguntas && !podeSalvar() && !concluir) {
      toast({
        title: "Nada para salvar",
        description: "Preencha pelo menos uma pergunta ou adicione uma nota antes de salvar.",
        variant: "destructive"
      });
      return;
    }

    // Se for para concluir, validar se o formulário está completamente preenchido (apenas se houver perguntas)
    if (concluir && temPerguntas) {
      if (!formularioCompleto()) {
        const validacao = validarFormulario();
        const pendenciasMsg = validacao.pendencias.length > 0 
          ? ` Pendências: ${validacao.pendencias.join(', ')}`
          : ' Por favor, responda todas as perguntas do formulário antes de concluir.';
        toast({
          title: "Formulário incompleto",
          description: `Não é possível concluir o formulário enquanto houver perguntas pendentes.${pendenciasMsg}`,
          variant: "destructive"
        });
        return;
      }
    }

    setLoading(true);

    try {
      // Preparar respostas para envio
      const respostasFormulario: RespostaFormulario[] = Object.entries(respostas)
        .filter(([perguntaId, resposta]) => {
          const isValid = resposta && resposta.trim() !== '';
          return isValid;
        })
        .map(([perguntaId, resposta]) => ({
          pergunta_id: perguntaId,
          resposta: resposta.trim()
        }));

      // Verificar se há perguntas no formulário
      const temPerguntasFormulario = (tarefa.perguntas_formulario && tarefa.perguntas_formulario.length > 0) || perguntasOrdenadas.length > 0;
      
      // Determinar o status: concluída se o usuário quiser concluir E (não houver perguntas OU o formulário estiver completo),
      // caso contrário, manter como em_andamento ou atual para salvar progresso
      const podeConcluir = concluir && (!temPerguntasFormulario || formularioCompleto());
      const statusFinal = podeConcluir
        ? 'concluida' 
        : (tarefa.status === 'concluida' || tarefa.status === 'concluida_com_pendencias' 
          ? tarefa.status 
          : 'em_andamento');

      // Avaliar conclusões automáticas se o template tiver definições
      // A conclusão só deve ser avaliada se o formulário estiver completo ou se estamos concluindo a tarefa
      let conclusaoAutomatica: string | null = null;
      const formularioEstaCompleto = formularioCompleto();
      
      // Só avaliar conclusões se o formulário estiver completo ou se estamos concluindo
      const deveAvaliarConclusao = formularioEstaCompleto || concluir;
      
      if (template?.conclusoes_automaticas && template.conclusoes_automaticas.length > 0 && deveAvaliarConclusao) {
        console.log('🔍 PreencherFormularioModal: Iniciando avaliação de conclusões automáticas');
        console.log('🔍 PreencherFormularioModal: Formulário completo:', formularioEstaCompleto, '| Concluindo:', concluir);
        console.log('🔍 PreencherFormularioModal: Template conclusões:', template.conclusoes_automaticas);
        console.log('🔍 PreencherFormularioModal: Respostas disponíveis:', Object.keys(respostas).length, 'respostas');
        console.log('🔍 PreencherFormularioModal: IDs das respostas:', Object.keys(respostas));
        
        // Usar perguntas expandidas se disponíveis, senão usar perguntas do template
        const perguntasParaAvaliar = perguntasExpandidas.length > 0 ? perguntasExpandidas : (template.perguntas_formulario || []);
        conclusaoAutomatica = avaliarConclusoesAutomaticas(template.conclusoes_automaticas, respostas, perguntasParaAvaliar);
        
        console.log('🔍 PreencherFormularioModal: Resultado da avaliação:', conclusaoAutomatica || 'Nenhuma conclusão encontrada');
      } else {
        if (!template?.conclusoes_automaticas || template.conclusoes_automaticas.length === 0) {
          console.log('⚠️ PreencherFormularioModal: Template não possui conclusões automáticas definidas');
        } else if (!deveAvaliarConclusao) {
          console.log('⚠️ PreencherFormularioModal: Não avaliando conclusões - formulário incompleto e não está concluindo');
        }
        // Manter a conclusão existente se já houver uma salva
        conclusaoAutomatica = tarefa?.conclusao_automatica || null;
      }

      const payload = {
        respostas_formulario: respostasFormulario,
        status: statusFinal,
        notas: notas || null,
        conclusao_automatica: conclusaoAutomatica || null
      };

      console.log('📤 PreencherFormularioModal: Payload a ser enviado:', {
        respostas_count: respostasFormulario.length,
        respostas_ids: respostasFormulario.map(r => r.pergunta_id),
        status: statusFinal,
        tem_notas: !!notas,
        conclusao_automatica: conclusaoAutomatica || null,
        payload_completo: payload
      });

      const response = await fetchWithAuth(`/api/tarefas/${tarefa.id}/formulario`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Erro na resposta:', error);
        throw new Error(error.error || 'Erro ao salvar formulário');
      }

      const responseData = await response.json();

      // Verificar se a tarefa foi concluída (temPerguntasFormulario já foi declarada acima)
      const foiConcluida = concluir && (!temPerguntasFormulario || formularioCompleto());

      if (foiConcluida) {
        toast({
          title: "Sucesso",
          description: temPerguntasFormulario 
            ? "Formulário preenchido e tarefa concluída com sucesso!"
            : "Tarefa concluída com sucesso!"
        });
        
        // Disparar evento customizado para atualizar PipelineWidget quando tarefa é concluída
        if (tarefa.lead?.id) {
          window.dispatchEvent(new CustomEvent('tarefaConcluida', { 
            detail: { tarefaId: tarefa.id, leadId: tarefa.lead.id } 
          }));
        }
        
        onSuccess();
        onOpenChange(false);
        setRespostas({});
      } else {
        toast({
          title: "Progresso salvo",
          description: "Seu progresso foi salvo. Você pode continuar preenchendo o formulário depois."
        });
        // Atualizar a tarefa localmente com as respostas salvas
        onSuccess();
      }
    } catch (error: any) {
      console.error('Erro ao salvar formulário:', error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível salvar o formulário",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Não limpar respostas aqui para preservar progresso salvo
      // As respostas serão recarregadas quando o modal for reaberto
      setPerguntaAtualId(null);
      setPerguntasRespondidas(new Set());
      setFluxoPerguntas([]);
      setModoEdicao(false);
      // Manter notas se houver, senão limpar
      if (!tarefa?.notas) {
        setNotas('');
      }
      onOpenChange(false);
    }
  };

  const handleIniciarEdicao = () => {
    if (!tarefa) return;
    
    // Carregar respostas salvas no estado de edição
    const respostasExistentes = tarefa.respostas_formulario
      ? respostasParaMapa(tarefa.respostas_formulario)
      : {};
    
    setRespostas(respostasExistentes);
    
    // Calcular fluxo e definir primeira pergunta
    const perguntasParaUsar = perguntasExpandidas.length > 0 ? perguntasExpandidas : (tarefa.perguntas_formulario || []);
    const respostasMap = respostasExistentes;
    const fluxo = calcularFluxoPerguntas(perguntasParaUsar, respostasMap, blocosMap);
    setFluxoPerguntas(fluxo);
    
    // Definir primeira pergunta como atual
    if (fluxo.length > 0) {
      setPerguntaAtualId(fluxo[0]);
    }
    
    // Marcar perguntas respondidas
    const respondidas = new Set<string>(Object.keys(respostasMap).filter(k => respostasMap[k] && respostasMap[k].trim() !== ''));
    setPerguntasRespondidas(respondidas);
    
    setModoEdicao(true);
  };

  const handleCancelarEdicao = () => {
    if (!tarefa) return;
    
    // Restaurar respostas originais
    const respostasExistentes = tarefa.respostas_formulario
      ? respostasParaMapa(tarefa.respostas_formulario)
      : {};
    
    setRespostas(respostasExistentes);
    setModoEdicao(false);
    setPerguntaAtualId(null);
  };

  const handleSalvarEdicao = async () => {
    if (!tarefa) return;
    
    try {
      setLoading(true);
      
      // Preparar respostas para envio
      const respostasFormulario: RespostaFormulario[] = Object.entries(respostas)
        .filter(([perguntaId, resposta]) => {
          const isValid = resposta && resposta.trim() !== '';
          return isValid;
        })
        .map(([perguntaId, resposta]) => ({
          pergunta_id: perguntaId,
          resposta: resposta.trim()
        }));

      const payload = {
        respostas_formulario: respostasFormulario,
        status: tarefa.status, // Manter o status atual
        notas: notas || null
      };

      const response = await fetchWithAuth(`/api/tarefas/${tarefa.id}/formulario`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erro ao salvar formulário');
      }

      toast({
        title: "Respostas atualizadas",
        description: "As respostas foram salvas com sucesso!"
      });
      
      // Atualizar a tarefa através do callback
      onSuccess();
      
      // Atualizar estado local
      const dadosAtualizados = await buscarDetalhesTarefa(tarefa.id);
      if (dadosAtualizados) {
        const respostasExistentes = dadosAtualizados.respostas_formulario
          ? respostasParaMapa(dadosAtualizados.respostas_formulario)
          : {};
        setRespostas(respostasExistentes);
      }
      
      setModoEdicao(false);
    } catch (error: any) {
      console.error('Erro ao salvar edição:', error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível salvar as alterações",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const perguntas = perguntasExpandidas.length > 0 ? perguntasExpandidas : (tarefa?.perguntas_formulario || []);
  const perguntasOrdenadas = fluxoPerguntas
    .map(id => perguntas.find(p => p.id === id))
    .filter((p): p is PerguntaFormulario => Boolean(p));
  const validacao = validarFormulario();
  const podeExibirFormulario = !!tarefa && !isLoading;
  const temConteudoParaSalvar = podeSalvar();
  const estaCompleto = formularioCompleto();
  
  // Verificar se a tarefa possui formulário de perguntas
  const temFormularioPerguntas = perguntasOrdenadas.length > 0 || (tarefa?.perguntas_formulario && tarefa.perguntas_formulario.length > 0);
  
  // Verificar se a tarefa está concluída e tem respostas salvas
  const tarefaConcluida = tarefa?.status === 'concluida' || tarefa?.status === 'concluida_com_pendencias';
  const temRespostasSalvas = tarefa?.respostas_formulario && tarefa.respostas_formulario.length > 0;
  const modoVisualizacao = tarefaConcluida && temRespostasSalvas && !modoEdicao;
  const isAvulsa = Boolean(tarefa && !tarefa.template_id);

  const renderLoadingState = () => (
    <div className="flex h-48 items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <span className="text-sm font-medium">Carregando formulário atualizado...</span>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent
        className={cn(
          'flex flex-col gap-0 p-0 overflow-hidden shadow-2xl max-h-[min(92vh,880px)]',
          'w-[min(100vw-0.5rem,960px)] max-w-[960px] translate-x-[-50%] translate-y-[-50%] sm:rounded-lg',
          'border bg-background'
        )}
      >
        <DialogTitle className="sr-only">
          {tarefa ? `Tarefa: ${tarefa.titulo}` : 'Detalhe da tarefa'}
        </DialogTitle>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex shrink-0 items-center justify-between gap-2 border-b bg-background px-3 py-2">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span className="inline-flex max-w-[min(100%,280px)] items-center gap-1.5 truncate rounded-md border bg-muted/40 px-2.5 py-1 text-xs font-medium text-foreground">
                {tarefa ? KANBAN_LIST_LABELS[getKanbanColumnForTask(tarefa)] : 'Quadro'}
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-0.5">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground"
                disabled
                title="Em breve"
              >
                <ImageIcon className="h-4 w-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Mais opções">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  {tarefa?.lead?.id && onVerLead ? (
                    <DropdownMenuItem onClick={() => onVerLead(tarefa.lead!.id)}>Ver ficha do lead</DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem
                    onClick={() => {
                      void navigator.clipboard.writeText(tarefa?.titulo ?? '').catch(() => {});
                      toast({ title: 'Título copiado' });
                    }}
                  >
                    Copiar título
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() =>
                      document.getElementById('tarefa-sec-formulario')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                  >
                    Ir ao formulário
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      document.getElementById('tarefa-sec-notas')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                  >
                    Ir às notas
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DialogClose asChild>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Fechar">
                  <X className="h-4 w-4" />
                </Button>
              </DialogClose>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
            <div className="min-h-0 flex-1 min-w-0 overflow-y-auto">
              <div className="space-y-4 px-4 py-3 pb-4">
            {isLoading && !tarefa ? renderLoadingState() : (
              <>
                {tarefa && (
                  <div className="-mx-1 space-y-4 border-b border-border/50 px-1 pb-4">
                    <div className="flex items-start gap-3">
                      <button
                        type="button"
                        className={cn(
                          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                          tarefaConcluida
                            ? 'border-emerald-600 bg-emerald-600 text-white'
                            : 'border-muted-foreground/40 hover:border-primary'
                        )}
                        onClick={() => {
                          if (!tarefa) return;
                          if (temFormularioPerguntas) {
                            document.getElementById('tarefa-sec-formulario')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            toast({
                              title: 'Formulário pendente',
                              description: 'Complete o formulário e use Salvar e Concluir para fechar a tarefa.',
                            });
                            return;
                          }
                          if (tarefa.template_id) {
                            toast({
                              title: 'Tarefa de template',
                              description: 'Altere o status pelo quadro ou pelos botões no rodapé.',
                            });
                          }
                        }}
                        aria-label={tarefaConcluida ? 'Tarefa concluída' : 'Ver conclusão do formulário'}
                      >
                        {tarefaConcluida ? <Check className="h-4 w-4" /> : null}
                      </button>
                      <div className="min-w-0 flex-1 space-y-1">
                        {isAvulsa && !modoVisualizacao ? (
                          <Input
                            value={tituloEdit}
                            onChange={(e) => setTituloEdit(e.target.value)}
                            onBlur={() => {
                              const t = tituloEdit.trim();
                              if (t && t !== tarefa.titulo) void salvarMetaAvulsa({ titulo: t });
                            }}
                            className="h-auto border-0 bg-transparent px-0 text-xl font-semibold shadow-none focus-visible:ring-0 md:text-2xl"
                            disabled={loading || metaSaving}
                          />
                        ) : (
                          <h2 className="text-xl font-semibold leading-tight text-foreground md:text-2xl">{tarefa.titulo}</h2>
                        )}
                        <div className="flex flex-wrap gap-1.5 pt-0.5">
                          {renderStatusBadge(tarefa.status)}
                          {renderPrioridadeBadge(tarefa.prioridade)}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() =>
                          document.getElementById('tarefa-sec-notas')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        }
                      >
                        <Plus className="mr-1 h-3.5 w-3.5" />
                        Adicionar
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="h-8 text-xs" disabled title="Em breve">
                        <Tag className="mr-1 h-3.5 w-3.5" />
                        Etiquetas
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() =>
                          document.getElementById('tarefa-sec-datas')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        }
                      >
                        <Calendar className="mr-1 h-3.5 w-3.5" />
                        Datas
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() =>
                          document.getElementById('tarefa-sec-formulario')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                        }
                      >
                        <CheckSquare className="mr-1 h-3.5 w-3.5" />
                        Checklist
                      </Button>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <AlignLeft className="h-4 w-4 text-muted-foreground" />
                        Descrição
                      </div>
                      {isAvulsa && !modoVisualizacao ? (
                        <div className="space-y-2">
                          <Textarea
                            value={descricaoEdit}
                            onChange={(e) => setDescricaoEdit(e.target.value)}
                            placeholder="Adicionar uma descrição mais detalhada…"
                            rows={5}
                            className="min-h-[120px] resize-y text-sm"
                            disabled={loading || metaSaving}
                          />
                          <div className="flex justify-end">
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={
                                metaSaving ||
                                descricaoEdit.trim() === (tarefa.descricao || tarefa.instrucoes || '').trim()
                              }
                              onClick={() => void salvarMetaAvulsa({ descricao: descricaoEdit })}
                            >
                              Guardar descrição
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Textarea
                          readOnly
                          value={(tarefa.descricao || tarefa.instrucoes || '').trim() ? tarefa.descricao || tarefa.instrucoes || '' : ''}
                          placeholder="Adicionar uma descrição mais detalhada…"
                          rows={4}
                          className="min-h-[100px] resize-none bg-muted/20 text-sm"
                        />
                      )}
                    </div>
                  </div>
                )}
                {isLoading && (
                  <Card className="border-dashed">
                    <CardContent className="py-8">
                      {renderLoadingState()}
                    </CardContent>
                  </Card>
                )}
                {tarefa && (
                  <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ClipboardList className="h-4 w-4 text-primary" />
                      Informações da Tarefa
                    </CardTitle>
                    <DialogDescription className="text-sm">
                      Consulte os dados do template e confirme todas as informações antes de registrar as respostas do cliente.
                    </DialogDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-1">
                        <span className="text-xs font-semibold uppercase text-muted-foreground">Prioridade</span>
                        {renderPrioridadeBadge(tarefa.prioridade)}
                      </div>
                      <div className="space-y-1">
                        <span className="text-xs font-semibold uppercase text-muted-foreground">Status atual</span>
                        {renderStatusBadge(tarefa.status)}
                      </div>
                      <div className="space-y-1">
                        <span className="text-xs font-semibold uppercase text-muted-foreground">Tipo de tarefa</span>
                        <Badge variant="secondary" className="w-fit">
                          {tarefa.tipo_tarefa}
                        </Badge>
                      </div>
                      <div className="space-y-1">
                        <span className="text-xs font-semibold uppercase text-muted-foreground">Etapa do funil</span>
                        <Badge variant="outline" className="w-fit">
                          {getEtapaNome(tarefa.etapa_criacao)}
                        </Badge>
                      </div>
                      {tarefa.template_id && (
                        <div className="space-y-1">
                          <span className="text-xs font-semibold uppercase text-muted-foreground">Template</span>
                          <span className="text-sm text-muted-foreground">
                            #{tarefa.template_id}
                          </span>
                        </div>
                      )}
                      <div className="space-y-1">
                        <span className="text-xs font-semibold uppercase text-muted-foreground">Criada em</span>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(tarefa.data_criacao)}</span>
                        </div>
                      </div>
                    </div>

                    <Separator />

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <span className="text-xs font-semibold uppercase text-muted-foreground">
                          Lead associado
                        </span>
                        {tarefa.lead ? (
                          <div className="space-y-2 rounded-md border p-3">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              {tarefa.lead.tipo === 'juridica' ? (
                                <Building className="h-4 w-4 text-green-600" />
                              ) : (
                                <User className="h-4 w-4 text-blue-600" />
                              )}
                              <span>{tarefa.lead.nome}</span>
                            </div>
                            <div className="text-xs text-muted-foreground space-y-1">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">{getEtapaNome(tarefa.lead.etapa)}</Badge>
                              </div>
                              {tarefa.lead.telefone && (
                                <div className="flex items-center gap-2">
                                  <Phone className="h-3.5 w-3.5" />
                                  <span>{formatPhone(tarefa.lead.telefone)}</span>
                                </div>
                              )}
                              {tarefa.lead.email && (
                                <div className="flex items-center gap-2">
                                  <FileText className="h-3.5 w-3.5" />
                                  <span>{tarefa.lead.email}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">Nenhum lead vinculado.</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <span className="text-xs font-semibold uppercase text-muted-foreground">
                          Vendedor responsável
                        </span>
                        {tarefa.vendedor ? (
                          <div className="rounded-md border p-3 text-sm font-medium flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span>{tarefa.vendedor.nome}</span>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">Nenhum vendedor atribuído.</p>
                        )}
                      </div>
                    </div>

                    <Separator />

                    <div id="tarefa-sec-datas" className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-1">
                        <span className="text-xs font-semibold uppercase text-muted-foreground">Início</span>
                        <span className="text-sm text-muted-foreground">{formatDate(tarefa.data_inicio)}</span>
                      </div>
                      <div className="space-y-1">
                        <span className="text-xs font-semibold uppercase text-muted-foreground">Prazo</span>
                        <span className="text-sm text-muted-foreground">{formatDate(tarefa.data_vencimento)}</span>
                        {typeof tarefa.dias_restantes === 'number' && (
                          <span className="text-xs font-semibold">
                            {tarefa.dias_restantes < 0
                              ? `${Math.abs(tarefa.dias_restantes)} dia(s) atrasado`
                              : tarefa.dias_restantes === 0
                              ? 'Vence hoje'
                              : tarefa.dias_restantes === 1
                              ? 'Vence amanhã'
                              : `${tarefa.dias_restantes} dias restantes`}
                          </span>
                        )}
                      </div>
                      <div className="space-y-1">
                        <span className="text-xs font-semibold uppercase text-muted-foreground">Conclusão</span>
                        <span className="text-sm text-muted-foreground">{formatDate(tarefa.data_conclusao)}</span>
                      </div>
                      {typeof tarefa.tempo_gasto_minutos === 'number' && (
                        <div className="space-y-1">
                          <span className="text-xs font-semibold uppercase text-muted-foreground">Tempo registrado</span>
                          <span className="text-sm text-muted-foreground">{tarefa.tempo_gasto_minutos} minutos</span>
                        </div>
                      )}
                    </div>

                    {(tarefa.instrucoes || tarefa.observacoes || tarefa.resultado_acao) && (
                      <>
                        <Separator />
                        {tarefa.instrucoes && (
                          <div className="space-y-2">
                            <span className="text-xs font-semibold uppercase text-muted-foreground">
                              Instruções do template
                            </span>
                            <p className="rounded-md border bg-muted/20 p-3 text-sm whitespace-pre-line">
                              {tarefa.instrucoes}
                            </p>
                          </div>
                        )}
                        {tarefa.observacoes && (
                          <div className="space-y-2">
                            <span className="text-xs font-semibold uppercase text-muted-foreground">
                              Observações internas
                            </span>
                            <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground whitespace-pre-line">
                              {tarefa.observacoes}
                            </p>
                          </div>
                        )}
                        {tarefa.resultado_acao && (
                          <div className="space-y-2">
                            <span className="text-xs font-semibold uppercase text-muted-foreground">
                              Resultado anterior registrado
                            </span>
                            <p className="rounded-md border p-3 text-sm whitespace-pre-line">
                              {tarefa.resultado_acao}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
                )}

                <div id="tarefa-sec-formulario" className="space-y-4">
                {podeExibirFormulario && perguntasOrdenadas.length === 0 && !modoVisualizacao ? (
                  <Card>
                    <CardContent className="pt-6 text-center text-muted-foreground">
                      <p>Esta tarefa não possui formulário de perguntas.</p>
                    </CardContent>
                  </Card>
                ) : modoVisualizacao ? (
                  // Modo de visualização: mostrar todas as respostas salvas
                  (() => {
                    const respostasMap = respostasParaMapa(tarefa!.respostas_formulario!);
                    const todasPerguntas = perguntas;
                    
                    // Criar um mapa de perguntas para busca rápida
                    const mapaPerguntas = new Map(todasPerguntas.map(p => [p.id, p]));
                    
                    // Obter todas as perguntas que têm respostas, ordenadas
                    const perguntasComRespostas = Object.keys(respostasMap)
                      .map(id => mapaPerguntas.get(id))
                      .filter((p): p is PerguntaFormulario => Boolean(p))
                      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

                    return (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            Respostas do Formulário
                          </CardTitle>
                          <p className="text-sm text-muted-foreground">
                            Visualize todas as respostas registradas para esta tarefa.
                          </p>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {perguntasComRespostas.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              Nenhuma resposta encontrada.
                            </p>
                          ) : (
                            perguntasComRespostas.map((pergunta, index) => {
                              const resposta = respostasMap[pergunta.id] || '';
                              return (
                                <div key={pergunta.id} className="space-y-2 border-b pb-4 last:border-b-0">
                                  <div className="flex items-start gap-2">
                                    <span className="text-xs font-semibold text-muted-foreground min-w-[40px]">
                                      {index + 1}.
                                    </span>
                                    <div className="flex-1">
                                      <p className="text-sm font-medium mb-2">{pergunta.texto}</p>
                                      <div className="rounded-md border bg-muted/30 p-3">
                                        {pergunta.tipo_resposta === 'multipla_escolha' ? (
                                          <div className="flex items-center gap-2">
                                            <div className="h-2 w-2 rounded-full bg-primary" />
                                            <span className="text-sm">{resposta}</span>
                                          </div>
                                        ) : (
                                          <p className="text-sm whitespace-pre-wrap">{resposta}</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          )}
                          {perguntasComRespostas.length > 0 && (
                            <div className="pt-4 border-t">
                              <Button
                                onClick={handleIniciarEdicao}
                                variant="outline"
                                className="w-full"
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Editar Respostas
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })()
                ) : null}

                {/* Exibir Conclusão Automática quando disponível - após as respostas do formulário */}
                {tarefa?.conclusao_automatica && modoVisualizacao && (
                  <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 text-primary" />
                        Conclusão Automática
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        Conclusão gerada automaticamente com base nas respostas do formulário.
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-lg border-2 border-primary/30 bg-background shadow-sm">
                        <Badge variant="default" className="text-base font-semibold px-4 py-2 w-fit">
                          {tarefa.conclusao_automatica}
                        </Badge>
                        <span className="text-sm text-muted-foreground flex-1">
                          Esta conclusão foi aplicada automaticamente quando as perguntas de fim correspondentes foram respondidas.
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {!modoVisualizacao && (modoEdicao || podeExibirFormulario) && perguntaAtualId ? (
                  (() => {
                    const perguntaAtual = perguntasOrdenadas.find(p => p.id === perguntaAtualId);
                    if (!perguntaAtual) return null;

                    const respostaAtual = respostas[perguntaAtual.id] || '';
                    const isRespondida = respostaAtual.trim() !== '';
                    const indexAtual = perguntasOrdenadas.findIndex(p => p.id === perguntaAtualId);

                    return (
                      <Card key={perguntaAtual.id}>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between gap-2">
                            <CardTitle className="text-base flex items-center gap-2">
                              <span className="text-muted-foreground">Pergunta {indexAtual + 1} de {perguntasOrdenadas.length}</span>
                            </CardTitle>
                            {!isRespondida && <AlertCircle className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                            {isRespondida && (
                              <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-sm font-medium mt-2">{perguntaAtual.texto}</p>
                        </CardHeader>
                        <CardContent>
                          {perguntaAtual.tipo_resposta === 'multipla_escolha' ? (
                            <RadioGroup
                              value={respostaAtual}
                              onValueChange={(value) => handleRespostaMultiplaEscolha(perguntaAtual.id, value)}
                            >
                              {perguntaAtual.opcoes?.map((opcao, opcaoIndex) => (
                                <div key={opcaoIndex} className="flex items-center space-x-2 mb-2">
                                  <RadioGroupItem value={opcao} id={`${perguntaAtual.id}-${opcaoIndex}`} />
                                  <Label
                                    htmlFor={`${perguntaAtual.id}-${opcaoIndex}`}
                                    className="cursor-pointer flex-1"
                                  >
                                    {opcao}
                                  </Label>
                                </div>
                              ))}
                            </RadioGroup>
                          ) : (
                            <Textarea
                              value={respostaAtual}
                              onChange={(e) => handleRespostaDiscursiva(perguntaAtual.id, e.target.value)}
                              onBlur={(e) => handleRespostaDiscursivaBlur(perguntaAtual.id, e.target.value)}
                              placeholder="Digite sua resposta aqui..."
                              rows={4}
                            />
                          )}
                        </CardContent>
                        <CardContent className="pt-0 flex justify-between items-center border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handlePerguntaAnterior}
                            disabled={indexAtual === 0}
                          >
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Anterior
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={handleProximaPergunta}
                            disabled={modoEdicao ? false : !isRespondida}
                          >
                            Próxima
                            <ArrowRight className="h-4 w-4 ml-2" />
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })()
                ) : (modoEdicao || podeExibirFormulario) && (
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4" />
                      <p className="text-lg font-semibold mb-2">Formulário Completo!</p>
                        <p className="text-sm text-muted-foreground">
                          Todas as perguntas foram respondidas. Você pode revisar suas respostas ou salvar o formulário.
                        </p>
                    </CardContent>
                  </Card>
                )}
                </div>
                {(!modoVisualizacao || modoEdicao) ? (
                  <Card id="tarefa-sec-notas">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Layers className="h-4 w-4 text-primary" />
                        Notas da execução
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        Registre comentários relevantes sobre a tarefa realizada. As notas ficam disponíveis para consultas posteriores.
                      </p>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        value={notas}
                        onChange={(e) => setNotas(e.target.value)}
                        placeholder="Adicione observações sobre o atendimento, follow-ups ou aprendizados."
                        rows={4}
                        disabled={loading || isLoading}
                      />
                    </CardContent>
                  </Card>
                ) : tarefa?.notas ? (
                  <Card id="tarefa-sec-notas">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Layers className="h-4 w-4 text-primary" />
                        Notas da execução
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="rounded-md border bg-muted/30 p-3">
                        <p className="text-sm whitespace-pre-wrap">{tarefa.notas}</p>
                      </div>
                    </CardContent>
                  </Card>
                ) : null}
              </>
            )}
          </div>
        </div>

        <aside className="flex w-full shrink-0 flex-col border-t bg-[#f4f5f7] dark:bg-muted/30 md:w-[min(100%,320px)] md:border-l md:border-t-0">
          <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
            <div className="flex items-start justify-between gap-2">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                Comentários e atividade
              </h3>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 px-2 text-xs"
                onClick={() => setActivityDetails((v) => !v)}
              >
                {activityDetails ? 'Ocultar detalhes' : 'Mostrar detalhes'}
              </Button>
            </div>
            <Input
              placeholder="Escrever um comentário…"
              disabled
              className="bg-background text-sm"
              title="Em breve"
            />
            {tarefa ? (
              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto text-sm">
                <div className="flex gap-2">
                  <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-500 text-[10px] font-semibold text-white"
                    aria-hidden
                  >
                    {iniciaisNome(tarefa.vendedor?.nome ?? '?')}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="leading-snug text-foreground">
                      <span className="font-medium">{tarefa.vendedor?.nome ?? 'Alguém'}</span>{' '}
                      {tarefa.template_id ? 'criou esta tarefa a partir de um modelo' : 'criou esta tarefa'}
                      {tarefa.lead?.nome ? (
                        <>
                          {' '}
                          no lead <span className="font-medium">{tarefa.lead.nome}</span>
                        </>
                      ) : null}
                      .
                    </p>
                    <button
                      type="button"
                      className="mt-1 text-left text-xs text-primary hover:underline"
                      onClick={() => setActivityDetails(true)}
                    >
                      {new Date(tarefa.data_criacao).toLocaleString('pt-BR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </button>
                    {activityDetails ? (
                      <div className="mt-2 space-y-1 border-l-2 border-muted-foreground/25 pl-2 text-xs text-muted-foreground">
                        <p>
                          Lista:{' '}
                          <span className="font-medium text-foreground">
                            {KANBAN_LIST_LABELS[getKanbanColumnForTask(tarefa)]}
                          </span>
                        </p>
                        <p>
                          Status: <span className="font-medium text-foreground">{tarefa.status}</span>
                        </p>
                        {tarefa.data_conclusao ? (
                          <p>Concluída em {formatDate(tarefa.data_conclusao)}</p>
                        ) : null}
                        <p>Etapa (criação): {getEtapaNome(tarefa.etapa_criacao)}</p>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Carregando atividade…</p>
            )}
          </div>
        </aside>
      </div>

        {/* Botões */}
        <div className="flex-none px-6 py-4 border-t flex justify-end bg-muted/20">
          {modoEdicao ? (
            <>
              <Button
                variant="outline"
                onClick={handleCancelarEdicao}
                disabled={loading}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Cancelar Edição
              </Button>
              <Button
                onClick={handleSalvarEdicao}
                disabled={loading || !temConteudoParaSalvar}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Salvar Alterações
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                <X className="h-4 w-4 mr-2" />
                {modoVisualizacao ? 'Fechar' : 'Cancelar'}
              </Button>
              {!modoVisualizacao && (
                <>
                  {/* Botão Salvar (progresso parcial) */}
                  {temConteudoParaSalvar && !estaCompleto && (
                    <Button
                      onClick={() => handleSalvar(false)}
                      disabled={loading || !podeExibirFormulario || !temConteudoParaSalvar}
                      variant="outline"
                      className="border-green-600 text-green-600 hover:bg-green-50"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2" />
                          Salvando...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Salvar Progresso
                        </>
                      )}
                    </Button>
                  )}
                  {/* Botão Salvar e Concluir - habilitado quando o formulário está completo OU quando não há formulário de perguntas */}
                  <Button
                    onClick={() => handleSalvar(true)}
                    disabled={
                      loading || 
                      !podeExibirFormulario || 
                      (temFormularioPerguntas && (!temConteudoParaSalvar || !estaCompleto))
                    }
                    className="bg-green-600 hover:bg-green-700"
                    title={
                      temFormularioPerguntas && !estaCompleto 
                        ? "Complete todas as perguntas do formulário para concluir" 
                        : temFormularioPerguntas && !temConteudoParaSalvar
                        ? "Preencha pelo menos uma pergunta ou adicione uma nota antes de concluir"
                        : ""
                    }
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Salvar e Concluir
                      </>
                    )}
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>
      </DialogContent>
    </Dialog>
  );
}

